import { useEffect, useState, type FormEvent } from 'react'
import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router'
import {
  login,
  signup,
  forgotPassword,
  verifySignup,
  completeSignup,
  verifyResetPassword,
  resetPassword,
  ApiError,
  bootstrapAuth,
} from '@/lib/apiClient'
import { consumeLoginOpensHome } from '@/lib/lastVisited'
import { toast } from '@/lib/toast'
import { ALLOWED_EMAIL_ERROR, isAllowedEmail } from '@/lib/allowedEmail'
import { useAuthStore } from '@/stores/authStore'
import { FoldableSwitch } from '@/components/layout/FoldableSwitch'
import { StandardLoginView } from '@/components/auth/StandardLoginView'
import { FoldableLoginView } from '@/components/auth/FoldableLoginView'
import type { LoginViewProps } from '@/components/auth/types'

interface LoginSearch {
  redirect?: string
  /** Emailed magic-link tokens — signup completion and password reset. */
  verify_token?: string
  reset_token?: string
}

const MIN_PASSWORD = 8

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
    verify_token:
      typeof search.verify_token === 'string' ? search.verify_token : undefined,
    reset_token:
      typeof search.reset_token === 'string' ? search.reset_token : undefined,
  }),
  // "Welcome back" is the LCP element. Waiting here on /auth/refresh holds
  // the heading until that response. A token already in memory still redirects.
  // A cookie-only session redirects from the page effect after first paint.
  beforeLoad: ({ search }) => {
    if (useAuthStore.getState().accessToken) {
      throw redirect({ to: search.redirect || '/', replace: true })
    }
  },
  component: LoginPage,
})

type AuthMode = 'signin' | 'signup' | 'forgot'

function headingFor(opts: {
  token?: string
  isReset: boolean
  mode: AuthMode
}): { title: string; subtitle: string } {
  if (opts.token) {
    return opts.isReset
      ? {
          title: 'Choose a new password',
          subtitle: 'Reset your DIOT AI password',
        }
      : {
          title: 'Set your password',
          subtitle: 'Finish setting up DIOT AI',
        }
  }
  if (opts.mode === 'signup') {
    return {
      title: 'Create your account',
      subtitle: 'Sign up for DIOT AI',
    }
  }
  if (opts.mode === 'forgot') {
    return {
      title: 'Reset your password',
      subtitle: 'Enter your email to receive a password reset link',
    }
  }
  return {
    title: 'Welcome back',
    subtitle: 'Sign in to DIOT AI',
  }
}

/**
 * Login — Vision AI card from iris-henna-phi.vercel.app (user 2026-09-01).
 * Persona tabs (Leadership / Service Provider / Segments) are reference-only
 * and are not shipped. Auth stays email + password.
 */
function LoginPage() {
  const navigate = useNavigate()
  const { verify_token, reset_token, redirect: redirectUrl } = Route.useSearch()
  const [mode, setMode] = useState<AuthMode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Sign-up is email-first (backend SignupInput): the verification link the
  // user receives leads to /complete-signup where the password is set.
  const [linkSent, setLinkSent] = useState(false)

  // Magic-link arrival: validate the token before showing the password form so
  // an expired link fails immediately instead of after the user types.
  const token = verify_token ?? reset_token
  const isReset = Boolean(reset_token)
  const [tokenState, setTokenState] = useState<
    'checking' | 'ready' | 'invalid'
  >(token ? 'checking' : 'ready')

  useEffect(() => {
    let live = true
    void bootstrapAuth().then((authed) => {
      if (!live || !authed) return
      void navigate({ to: (redirectUrl || '/') as never, replace: true })
    })
    return () => {
      live = false
    }
  }, [navigate, redirectUrl])

  useEffect(() => {
    if (!token) return
    let live = true
    const verify = isReset ? verifyResetPassword : verifySignup
    verify(token)
      .then((addr) => {
        if (!live) return
        setEmail(addr)
        setTokenState('ready')
      })
      .catch(() => {
        if (!live) return
        setTokenState('invalid')
        setError(
          isReset
            ? 'This password reset link is invalid or has expired.'
            : 'This sign-up link is invalid or has expired.',
        )
      })
    return () => {
      live = false
    }
  }, [token, isReset])

  const switchMode = (next: AuthMode) => {
    setMode(next)
    setEmail('')
    setPassword('')
    setError(null)
    setLinkSent(false)
  }

  const leaveTokenFlow = () => {
    setEmail('')
    setPassword('')
    setConfirm('')
    setError(null)
    setMode('signin')
    setLinkSent(false)
    void navigate({ to: '/login', search: {} })
  }

  const onSetPassword = async () => {
    if (!token) return
    setError(null)
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`)
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setBusy(true)
    try {
      await (isReset
        ? resetPassword(token, password)
        : completeSignup(token, password))
      if (redirectUrl) consumeLoginOpensHome()
      await navigate({ to: (redirectUrl || '/') as never, replace: true })
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? 'This link is invalid or has expired. Request a new one.'
          : err instanceof ApiError && err.status === 400
            ? 'Choose a password you have not used recently.'
            : 'Could not set your password. Try again.',
      )
      setBusy(false)
    }
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (token) {
      await onSetPassword()
      return
    }
    setError(null)
    // Password required for signin; sign-up and forgot-password need only email.
    if (!email || (mode === 'signin' && !password)) {
      setError(
        mode === 'signin'
          ? 'Enter your email and password.'
          : 'Enter your email.',
      )
      return
    }
    if (!isAllowedEmail(email)) {
      setError(ALLOWED_EMAIL_ERROR)
      return
    }
    setBusy(true)
    try {
      if (mode === 'signup') {
        await signup(email)
        setLinkSent(true)
        setBusy(false)
        return
      }
      if (mode === 'forgot') {
        await forgotPassword(email)
        setLinkSent(true)
        setBusy(false)
        toast('An email link was sent to your email to reset your password.')
        return
      }
      await login({ email, password })
      if (redirectUrl) consumeLoginOpensHome()
      if (redirectUrl && /^https?:\/\//i.test(redirectUrl)) {
        try {
          const urlObj = new URL(redirectUrl)
          await navigate({
            to: (urlObj.pathname + urlObj.search) as never,
            replace: true,
          })
        } catch {
          await navigate({ to: '/', replace: true })
        }
      } else if (redirectUrl) {
        await navigate({ to: redirectUrl as never, replace: true })
      } else {
        await navigate({ to: '/', replace: true })
      }
    } catch (err) {
      setError(
        mode === 'signup'
          ? 'Could not send the sign-up link. Try again.'
          : mode === 'forgot'
            ? 'Could not send the reset link. Try again.'
            : err instanceof ApiError && err.status === 401
              ? 'Invalid email or password.'
              : 'Something went wrong. Try again.',
      )
      setBusy(false)
    }
  }

  const { title, subtitle } = headingFor({ token, isReset, mode })

  const viewProps: LoginViewProps = {
    mode,
    email,
    setEmail,
    password,
    setPassword,
    confirm,
    setConfirm,
    error,
    busy,
    linkSent,
    setLinkSent,
    token,
    isReset,
    tokenState,
    title,
    subtitle,
    switchMode,
    leaveTokenFlow,
    onSubmit,
  }

  return (
    <FoldableSwitch
      default={<StandardLoginView {...viewProps} />}
      foldable={<FoldableLoginView {...viewProps} />}
    />
  )
}
